import {E, GetPercentFromXToY, Lerp, SleepAsync, SleepAsyncUntil, Timer} from "js-vextensions";
import React from "react";
import {Row, Text} from "react-vcomponents";
import {DesyncCalcType, EngineConfig_Breath, SirenType} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_Breath.js";
import {Belt_sampleInterval, Belt_samplesPerSecond, BreathProcessor} from "../../../UI/@Shared/Processors/BreathProcessor.js";
import {currentBelt} from "../../../UI/Tools/@Shared/BeltInterface.js";
import {GyroSample} from "../../../UI/Tools/@Shared/MuseInterface/GyroStructs.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {GDBelt_RawDataChunk, gdBelt_rawDataDB} from "../../../Utils/IndexedDB/GDBelt_RawDataDB.js";
import {PoliceSiren} from "./Breath/SirenSound.js";
import {NarrateText_ForEngineComp} from "../../../Utils/Services/TTS.js";
import moment from "moment";
import {EffectPointerPlayer} from "../../../Server/DB/@Shared/EffectPointerPlayer.js";
import {Log_Direct} from "../../../Utils/General/Logging.js";
import {AssertUnreachable, minuteInMS} from "web-vcore";
import {BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG, BREATH_INTERVAL_CALC_WINDOW_SIZE_SHORT} from "../../../UI/Timeline/Sessions/SessionUI/SessionChartColors.js";
import {BinarySearch} from "../../../Utils/General/General.js";
import {AlarmsComp, PhaseChange_ExtraMessage} from "./AlarmsComp.js";
import {MicroSnoozeComp} from "./MicroSnoozeComp.js";
import {store} from "../../../Store/index.js";
import {MemoryPalaceComp} from "./MemoryPalaceComp.js";
import {GeneralComp} from "./GeneralComp.js";
import {InAndroid, nativeBridge} from "../../../Utils/Bridge/Bridge_Native.js";
import {AlertsComp, EffectState_Light} from "./AlertsComp.js";
import {VisualizationComp} from "./VisualizationComp.js";
import {WebAudio_ForcedMute_Set, WebAudio_ForcedMute_Slow_Toggle, WebAudio_ForcedPan_Set, WebAudio_ForcedPan_Slow_Toggle} from "../../../Utils/Media/WebAudioPlayer.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";
import {SessionCoreData} from "../../../Server/DB/sessions/@Session.js";

export function TimeGapToRecencyStr(durationInMS: number|n) {
	return (
		durationInMS == null ? "n/a" :
		durationInMS < 1000 ? "just now" :
		durationInMS < 60000 ? `${Math.floor(durationInMS / 1000)}s ago` :
		`${Math.floor(durationInMS / 60000)}m ago`
	);
};

export const chunksWithStoragePending = [] as GDBelt_RawDataChunk[];

export type BreathCompMode = "fallingAsleep" | "dreaming";

export class BreathComp extends EngineSessionComp<EngineConfig_Breath> {
	constructor(session: LiveSession, config: EngineConfig_Breath) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
		this.Init_Siren();
		this.Init_Processors();
	}

	GetStatusUI() {
		const timeSinceLastBreathSample = this.lastForceSampleInfo != null ? Date.now() - this.lastForceSampleInfo.timeProcessed : null;
		const timeSinceBeltLoose = this.currentBeltLoose_startTime ? Date.now() - this.currentBeltLoose_startTime : null;
		//const lastBreathPeak = this.breathProcessor.breathPeaks.length > 0 ? this.breathProcessor.breathPeaks[this.breathProcessor.breathPeaks.length - 1] : null;
		//const breathInterval_short = this.breathProcessor.GetBreathIntervalForWindow(Date.now(), BREATH_INTERVAL_CALC_WINDOW_SIZE_SHORT, "average", this.breathProcessor.breathPeaks.length - 1);

		return <>
			<Row>Last breath sample: {TimeGapToRecencyStr(timeSinceLastBreathSample)}</Row>
			<Row mt={5}>Belt last off/too-loose at: {TimeGapToRecencyStr(timeSinceBeltLoose)}</Row>
			<Row mt={5}>Current breath-interval: {this.currentBreathInterval == null ? "n/a" : `${this.currentBreathInterval.RoundTo_Str(.1)}s`}</Row>
			<Row mt={5}>Current breath-interval LR: {this.currentBreathIntervalLowRange == null ? "n/a" : `${this.currentBreathIntervalLowRange.RoundTo_Str(.1)}s`}</Row>
		</>;
	}

	GetTriggerPackages() {
		return [
			new TriggerPackage("Breath_IncreaseCycleTime", this.c.sync_increaseCycleTime_triggerSet, this, {}, async triggerInfo=>{
				if (!this.c.sync_enabled) return;
				this.siren_cycleTime += this.c.sync_cycleTime_step;
				//this.siren.UpdateCycleTime(this.siren_cycleTime);
				this.StartOrRestartSiren();
				NarrateText_ForEngineComp(this, this.siren_cycleTime.RoundTo(.1).toString(), this.c.voice_volumeMultiplier);
			}),
			new TriggerPackage("Breath_DecreaseCycleTime", this.c.sync_decreaseCycleTime_triggerSet, this, {}, async triggerInfo=>{
				if (!this.c.sync_enabled) return;
				this.siren_cycleTime = (this.siren_cycleTime - this.c.sync_cycleTime_step).KeepAtLeast(.5);
				//this.siren.UpdateCycleTime(this.siren_cycleTime);
				this.StartOrRestartSiren();
				NarrateText_ForEngineComp(this, this.siren_cycleTime.RoundTo(.1).toString(), this.c.voice_volumeMultiplier);
			}),
			new TriggerPackage("Breath_IncreaseVolumeAtMinDesync", this.c.sync_increaseMinVolume_triggerSet, this, {}, async triggerInfo=>{
				if (!this.c.sync_enabled) return;
				this.siren_volume_min += this.c.sync_volume_min_step;
				this.DoCalcAndUpdateEffects();
				NarrateText_ForEngineComp(this, this.siren_volume_min.ToPercent(3) + "%", this.c.voice_volumeMultiplier);
			}),
			new TriggerPackage("Breath_DecreaseVolumeAtMinDesync", this.c.sync_decreaseMinVolume_triggerSet, this, {}, async triggerInfo=>{
				if (!this.c.sync_enabled) return;
				this.siren_volume_min -= this.c.sync_volume_min_step;
				this.DoCalcAndUpdateEffects();
				NarrateText_ForEngineComp(this, this.siren_volume_min.ToPercent(3) + "%", this.c.voice_volumeMultiplier);
			}),
			new TriggerPackage("Breath_BlockBreathIntervalCalc", this.c.sync_blockBreathIntervalEffects_triggerSet, this, {}, async triggerInfo=>{
				if (!this.c.sync_enabled) return;
				this.timeOfLastBreathIntervalCalcBlockStart = Date.now();
			}),
			new TriggerPackage("Breath_DeildAttemptStart", this.c.deild_attemptStart_triggerSet, this, {}, async triggerInfo=>{
				if (!this.c.sync_enabled) return;
				this.StartDeildAttempt();
			}),
		];
	}

	timeOfLastBreathIntervalCalcBlockStart: number|n;

	// drifting mode (these fields only get updated when in drifting mode)
	currentDesync = 0; // 0-1 (0 = no desync, 1 = full desync [ie. the user's inhale->exhale switch-point is exactly at the siren's low-point!])
	currentBreathInterval: number|n; // in seconds (null = no breath interval calculated yet)
	currentBreathIntervalLowRange: number|n; // in seconds (null = no breath interval low-range calculated yet)
	currentBeltLoose_startTime: number|n; // timestamp when continuous belt-loose warnings started (ie. when breath-interval became null)
	lastBeltLooseWarnTime: number|n;

	// dreaming mode
	dream_ender3_triggerTime: number|n; // timestamp when ender3 was triggered (ie. when BILR first exceeded dream_ender3_triggerBreathIntervalLowRange)

	// long-inhale detection
	// ==========

	doCalcAndUpdateEffectsTimer = new Timer(500, ()=>{
		this.DoCalcAndUpdateEffects();
	});
	DoCalcAndUpdateEffects() {
		const alarmsComp = this.s.Comp(AlarmsComp);
		const snoozeComp = this.s.Comp(MicroSnoozeComp);
		//const now = Date.now(); // just for debugging
		const now = this.breathProcessor.GetNowTime();
		//if (now == null) return;

		this.breathProcessor.UpdateBreathPeaks();

		const laterOfSessionStartAndLastSnooze = Math.max(this.s.coreData.startTime, snoozeComp.lastSnoozeExtensionTime ?? 0);
		const indexOfFirstForceSampleAfterSessionStartAndLastSnooze = BinarySearch(this.breathProcessor.samples_force, sample=>laterOfSessionStartAndLastSnooze - sample.time).GetIndexOfMatch_OrInsertPointPlusX(0);
		const forceSamplesAfterSessionStartAndLastSnooze = this.breathProcessor.samples_force.slice(indexOfFirstForceSampleAfterSessionStartAndLastSnooze);
		const breathTimeSinceStartOrSnooze = (forceSamplesAfterSessionStartAndLastSnooze.length / Belt_samplesPerSecond) * 1000;

		const breathIntervalCalcBlockActive = (now - (this.timeOfLastBreathIntervalCalcBlockStart ?? 0)) <= ((this.c.sync_blockBreathIntervalEffects_duration ?? 0) * minuteInMS);
		const allowBeltLooseDetection = store.main.testing.allowEarlyBreathIntervalEffects || (breathTimeSinceStartOrSnooze >= this.c.alert_beltLoose_detectionStart * minuteInMS && !breathIntervalCalcBlockActive);
		const allowBreathIntervalEffects = store.main.testing.allowEarlyBreathIntervalEffects || (breathTimeSinceStartOrSnooze >= BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG && !breathIntervalCalcBlockActive);

		// atm we only calculate the breath-interval when something is active that makes use of it
		const calcBreathInterval = allowBeltLooseDetection || allowBreathIntervalEffects;
		if (calcBreathInterval) {
			this.currentBreathInterval = this.breathProcessor.GetBreathIntervalForWindow(now, BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG, "average", this.breathProcessor.breathPeaks.length - 1);
			this.currentBreathIntervalLowRange = this.breathProcessor.GetBreathIntervalLowRangeForWindow(now);
		} else {
			// when not calculating breath-interval, unclear what we should do with the fields; seems slightly less confusing to clear them
			this.currentBreathInterval = null;
			this.currentBreathIntervalLowRange = null;
		}

		// if belt-loose detection is active, and belt is currently loose
		if (allowBeltLooseDetection && this.currentBreathInterval == null) {
			this.currentBeltLoose_startTime ??= now;

			// if belt-loose alert is enabled, and warn-interval is enabled, and we're in drift-like mode (don't want to risk mistaken effects in dreaming mode)
			// then try to "signal" the issue, by speaking a TTS message at the configured interval
			if (this.c.alert_beltLoose_enabled && this.c.alert_beltLoose_warnInterval > 0 && alarmsComp.PhaseIsDriftLike()) {
				const reachedTimeForNextWarning = now - (this.lastBeltLooseWarnTime ?? 0) >= this.c.alert_beltLoose_warnInterval * 1000;
				if (reachedTimeForNextWarning) {
					NarrateText_ForEngineComp(this, "Belt too loose", this.c.voice_volumeMultiplier);
					this.lastBeltLooseWarnTime = now;
				}
			}
		} else {
			this.currentBeltLoose_startTime = null;
		}

		// calculations/effects only needed in drift-like modes
		if (alarmsComp.PhaseIsDriftLike()) {
			this.breathProcessor.CalculateSirenStarts(this.s.coreData.events);

			const desync = this.breathProcessor.GetDesyncAmount_CombinedFromTwoWindows(now, true);
			// if there weren't enough samples to calculate the combined desync-amount (due to one of the windows not having enough samples), consider the combined desync amount to be 0 (so that sirens get reset to minimum volume/brightness)
			const desync_combined_final = desync.combined ?? 0;
			this.currentDesync = desync_combined_final;

			// if breath-interval has reached the dreaming-start threshold, switch to dreaming mode (if not already)
			if (allowBreathIntervalEffects && this.currentBreathInterval != null && this.c.dream_breathIntervalThreshold > 0 && this.currentBreathInterval < this.c.dream_breathIntervalThreshold) {
				alarmsComp.SetPhase(Phase.dreaming);
			}
			// if breath-interval low-range has reached the dreaming-start threshold, switch to dreaming mode (if not already)
			else if (allowBreathIntervalEffects && this.currentBreathIntervalLowRange != null && this.c.dream_startWhenBreathIntervalLowRangeBelow > 0 && this.currentBreathIntervalLowRange < this.c.dream_startWhenBreathIntervalLowRangeBelow) {
				alarmsComp.SetPhase(Phase.dreaming);
			}
		}
		// calculations/effects only needed in dreaming mode
		else if (alarmsComp.phase == "dreaming") {
			// if breath-interval has reached the dreaming-end threshold, switch to not-dreaming mode (ie. falling-asleep mode)
			if (allowBreathIntervalEffects && this.currentBreathInterval != null && this.c.dream_endWhenBreathIntervalAbove > 0 && this.currentBreathInterval > this.c.dream_endWhenBreathIntervalAbove) {
				alarmsComp.SetPhase(Phase.drifting);
			}
			// if breath-interval low-range has reached the dreaming-end threshold, switch to not-dreaming mode (ie. falling-asleep mode)
			else if (allowBreathIntervalEffects && this.currentBreathIntervalLowRange != null && this.c.dream_endWhenBreathIntervalLowRangeAbove > 0 && this.currentBreathIntervalLowRange > this.c.dream_endWhenBreathIntervalLowRangeAbove) {
				alarmsComp.SetPhase(Phase.drifting);
			}

			// ender3: two-threshold logic -- triggered when BILR exceeds the trigger-threshold, then ends dreaming mode after staying above the stay-above-threshold for the configured duration
			if (allowBreathIntervalEffects && this.c.dream_ender3_triggerBreathIntervalLowRange > 0) {
				if (this.currentBreathIntervalLowRange != null) {
					// if not yet triggered, check if BILR has crossed above the trigger threshold
					if (this.dream_ender3_triggerTime == null && this.currentBreathIntervalLowRange > this.c.dream_ender3_triggerBreathIntervalLowRange) {
						this.dream_ender3_triggerTime = now;
					}
					// if triggered, check if BILR has dropped below the stay-above threshold (reset if so)
					else if (this.dream_ender3_triggerTime != null && this.currentBreathIntervalLowRange < this.c.dream_ender3_stayAboveBreathIntervalLowRange) {
						this.dream_ender3_triggerTime = null;
					}
				} else {
					// if BILR is null (belt loose), reset trigger
					this.dream_ender3_triggerTime = null;
				}
				// if triggered and stay-above duration has elapsed, end dreaming mode
				if (this.dream_ender3_triggerTime != null) {
					const minutesSinceTrigger = (now - this.dream_ender3_triggerTime) / minuteInMS;
					if (minutesSinceTrigger >= this.c.dream_ender3_stayAboveDuration) {
						alarmsComp.SetPhase(Phase.drifting); // phase-change also clears dream_ender3_triggerTime
					}
				}
			}

			const minutesInDreamingMode = (now - alarmsComp.phase_changedAt) / minuteInMS;
			if (this.c.dream_endAfter != 0 && minutesInDreamingMode >= this.c.dream_endAfter) {
				alarmsComp.SetPhase(Phase.drifting);
			}
		}

		// long-inhale detection (eg. for starting DEILD attempts)
		this.breathProcessor.UpdateLongInhales(this.s.coreData.events, 30);
		// check if there are any new long-inhales detected
		const lastDetectedLongInhale = this.breathProcessor.longInhales.LastOrX();
		if (lastDetectedLongInhale != null) {
			const isNewLongInhale = this.lastLongInhaleSignalCompletionTime == null || lastDetectedLongInhale.timeOfSignalCompleted > this.lastLongInhaleSignalCompletionTime;
			if (isNewLongInhale) {
				this.OnLongInhaleDetected(lastDetectedLongInhale.timeOfSignalCompleted);
			}
		}

		// deild-attempt state-updating; check if we should end the current DEILD attempt (based on duration)
		if (this.deildAttemptActive && this.lastDeildAttemptStartTime != null) {
			const minutesInDeildAttempt = (now - this.lastDeildAttemptStartTime) / minuteInMS;
			if (minutesInDeildAttempt >= this.c.deild_attemptDuration) {
				// Switch back to drifting phase (falling-asleep mode); this results in deildAttemptActive also being reset to false
				alarmsComp.SetPhase(Phase.drifting);
			}
		}

		// Reset spatial_forcedPan and spatial_forcedMute if belt data is stale (>1s old since last sample)
		if (this.c.spatial_enabled) {
			const timeSinceLastSample_forSpatial = this.lastForceSampleInfo != null ? now - this.lastForceSampleInfo.timeProcessed : Infinity;
			if (timeSinceLastSample_forSpatial > 1000 && (this.spatial_forcedPan !== null || this.spatial_forcedMute !== null)) {
				this.UpdateForcedPanAndMuteStates(null, null, null);
			}
			// Periodically sync native pan state
		}

		// update alert states
		const alertsComp = this.s.Comp(AlertsComp);
		if (this.c.alert_beltDC_enabled) {
			alertsComp.SetEffectState("alert_breath_beltDC", EffectState_Light(this.IsBeltDisconnected(), this.c.alert_beltDC_lockLightsToBrightness, 0));
		}
		if (this.c.alert_beltLoose_enabled) {
			alertsComp.SetEffectState("alert_breath_beltLoose", EffectState_Light(
				this.IsBeltLooseForXOrLonger(0),
				this.IsBeltLooseForXOrLonger(this.c.alert_beltLoose_lockLightsAfter * minuteInMS) ? this.c.alert_beltLoose_lockLightsToBrightness : null,
				0
			));
		}

		// applying of calculations, as effects
		this.UpdateSirenEffect_BasedOnState();
		this.UpdateNonAlertLightEffect_BasedOnState();

		/*if (this.mode == "fallingAsleep") {
			Log_Direct(
				`Siren desync: ${this.currentDesync.ToPercentStr(0)} (long: ${desync.forLongWindow!.ToPercentStr(0)}, short: ${desync.forShortWindow!.ToPercentStr(0)})`,
				`@inDreamingMode:`, this.inDreamingMode,
				`@breathInterval:`, !calcBreathInterval ? "[not being updated atm]" : this.currentBreathInterval != null ? this.currentBreathInterval.toFixed(2) : "n/a",
				"@volume:", this.siren_volume.ToPercentStr(2), // high precision, because volume can go very low while still being audible
				"@brightness:", this.light_brightness.ToPercentStr(0) ?? "n/a",
				"@now:", moment(now).format("HH:mm:ss.SSS"),
				//"@breathSlopes:", sampleSlopesOverLastXSeconds_breath,
				//`@sirenSlopes:`, sampleSlopesOverLastXSeconds_siren,
			);
		}*/
	}

	lastLongInhaleSignalCompletionTime: number|n;
	OnLongInhaleDetected(timeOfSignalCompleted: number) {
		this.lastLongInhaleSignalCompletionTime = timeOfSignalCompleted;

		/*this.StartDeildAttempt(timeOfSignalCompleted);
		const visComp = this.s.Comp(VisualizationComp);
		if (visComp?.promptingForceEnabled) {
			visComp.StopPromptingForceEnable(visComp.c.forceEnabled_expireSilently);
		}*/

		// store this timeOfSignalCompleted variable, but only till this call-stack completes (for use in StartDeildAttempt below)
		// by clearing after this call-stack, we only use it in cases the user has long-inhales hooked up for deild-attempt-starts
		this.timeOfSignalCompleted_fromLongInhaleEarlierThisCallStack = timeOfSignalCompleted;
		setTimeout(()=>this.timeOfSignalCompleted_fromLongInhaleEarlierThisCallStack = null, 0);

		this.s.longInhaleListeners[0]?.(timeOfSignalCompleted);
	}
	timeOfSignalCompleted_fromLongInhaleEarlierThisCallStack: number|n;

	// DEILD attempt tracking
	// ==========

	deild_extraEffectPlayer = new EffectPointerPlayer();

	lastDeildAttemptStartTime: number|n; // timestamp when a DEILD-attempt was last started
	deildAttemptActive = false; // whether a DEILD attempt is currently in progress
	StartDeildAttempt() {
		const alarmsComp = this.s.Comp(AlarmsComp);
		// we don't have an (easy) way atm to directly pass "timeOfSignalCompleted", so use this hack
		const timeOfSignalCompleted = this.timeOfSignalCompleted_fromLongInhaleEarlierThisCallStack ?? Date.now();

		// Mark DEILD attempt as active
		this.deildAttemptActive = true;
		this.lastDeildAttemptStartTime = timeOfSignalCompleted;

		// Start a solve attempt (using the alarms comp)
		alarmsComp.NotifySolveProgress(this.c.deild_attemptDuration * 60); // convert minutes to seconds

		// Play the configured extra effect (eg. TTS message), if set
		if (this.c.deild_attemptStart_extraEffect) {
			this.deild_extraEffectPlayer.Play();
		}

		// Add an event to the session
		this.s.AsLocal!.AddEvent({type: "Breath.DeildAttemptStart"});
	}
	override OnLeavePhase_Dreaming(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage): void {
		this.dream_ender3_triggerTime = null;
	}
	override OnLeavePhase_Solving(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage): void {
		if (this.deildAttemptActive) {
			this.deildAttemptActive = false;
		}
	}

	siren!: PoliceSiren;
	siren_cycleTime!: number; // in seconds
	siren_volume_min!: number;
	siren_volume!: number;
	Init_Siren() {
		this.siren = new PoliceSiren();
		this.siren_cycleTime = this.c.sync_cycleTime;
		this.siren_volume_min = this.c.sync_volume_min;
		//this.siren_volume = this.c.sync_volume_min; // start at min-desync volume
		this.siren_volume = 0; // start at 0; volume will be updated soon
	}

	audioRealignmentTimer = new Timer(this.c.sync_siren_cycleCorrectionInterval * 1000, ()=>{
		this.RealignSirenEffectsWithLastSirenStart();
	}).SetContext(this.s.timerContext);
	async RealignSirenEffectsWithLastSirenStart() {
		if (this.siren.startTime_real <= 0) return void console.warn("Trying to do audio-realignment, but siren-start-time is 0/unset!"); // defensive

		let nextIntervalFromSirenStartTime = this.siren.startTime_real;
		while (nextIntervalFromSirenStartTime < Date.now()) {
			nextIntervalFromSirenStartTime += this.siren_cycleTime * 1000;
		}
		await SleepAsyncUntil(nextIntervalFromSirenStartTime);
		// only proceed with the siren-restart if the realignment-timer itself is still enabled (eg. so that if session is stopped, this doesn't restart the siren afterward!)
		if (!this.audioRealignmentTimer.Enabled) return;

		//this.StartOrRestartSiren(false); // don't add event, since this is just an audio correction/realignment
		// rather than call siren.Start (which would set siren.startTime_real -- which we don't want, as that could cause "drift" in the anchor-point), instead simply recreate the audio-nodes
		this.siren.CreateAudioNodes();

		this.StartSecondaryEffectsForNextCycleStart();
	}
	StartSecondaryEffectsForNextCycleStart() {
		this.startNextSirenCycleTimer?.Stop();
		this.CreateTimer_startNextSirenCycleTimer();
		this.startNextSirenCycleTimer!.Start(0);
	}
	startNextSirenCycleTimer: Timer|n;
	CreateTimer_startNextSirenCycleTimer() {
		const alarmsComp = this.s.Comp(AlarmsComp);
		const snoozeComp = this.s.Comp(MicroSnoozeComp);
		const memoryPalaceComp = this.s.Comp(MemoryPalaceComp);
		this.startNextSirenCycleTimer = new Timer(this.siren_cycleTime * 1000, async()=>{
			if (this.c.sync_sirenType == SirenType.memoryPalacePrompt) {
				for (let i = 0; i < this.c.sync_memPrompt_volumeSequence.length; i++) {
					const volume = this.c.sync_memPrompt_volumeSequence[i];
					// if not in drifting phase, or if snooze is active, don't actually play the prompt (we can still generate next prompt though, so that prompt timing/progression logic isn't impacted)
					const promptPlayAllowed = alarmsComp.phase == Phase.drifting && !snoozeComp.snooze_active;

					if (i == 0) {
						memoryPalaceComp.NewPrompt({playPrompt: promptPlayAllowed, volumeMultiplier: volume, promptPlayAddsEvent: false});
					} else {
						if (promptPlayAllowed) memoryPalaceComp.PlayPrompt(volume, false);
					}

					await SleepAsync(this.c.sync_memPrompt_interval * 1000);
					// todo: make system more robust to case of user putting more entries in volume-sequence than fits within the cycle-time (maybe just warning in UI, to allow overlaps if user really wants)
				}
			}
		}).SetContext(this.s.timerContext);
	}

	StartOrRestartSiren(addEvent = true) {
		this.siren.Start({
			cycleTime: this.siren_cycleTime,
			volume: this.siren_volume,
		});
		this.StartSecondaryEffectsForNextCycleStart();
		if (addEvent) this.s.AsLocal!.AddEvent({type: "Breath.SirenStart", sirenConfig: {cycleTime: this.siren_cycleTime}});
	}
	UpdateSirenEffect_BasedOnState() {
		// if siren-type is NOT police-siren, we actually still have it run, we just lock the volume to 0 // todo: eventually implement proper branching based on siren-type
		if (this.c.sync_sirenType != SirenType.siren) {
			this.siren_volume = 0;
			this.UpdateSirenEffect(false);
			return;
		}

		const phase = this.s.Comp(AlarmsComp).phase;
		if (phase == "drifting") {
			const percentFromMinToMaxVolume = GetPercentFromXToY(this.c.sync_desyncAmountForMinVolume, this.c.sync_desyncAmountForMaxVolume, this.currentDesync);
			this.siren_volume = Lerp(this.siren_volume_min, this.c.sync_volume_max, percentFromMinToMaxVolume);
			this.UpdateSirenEffect(true);
		} else if (phase == "dreaming") {
			this.siren_volume = 0;
			this.UpdateSirenEffect(false); // note: dreaming mode instantly ends once a snooze occurs (see MicroSnoozeComp.StartOrExtendSnooze())
		} else {
			this.siren_volume = 0;
			this.UpdateSirenEffect(false);
		}
	}
	UpdateSirenEffect(allowSnoozeToAffect: boolean) {
		let volume_final = this.siren_volume;

		if (allowSnoozeToAffect) {
			const snoozeComp = this.s.Comp(MicroSnoozeComp);
			// if snooze is active...
			if (snoozeComp.snooze_active) {
				// ...and phone is currently tilted (and not expired), then set volume to 0 (else a bit distracting while transcribing)
				if (snoozeComp.tiltSnooze_startTime != -1) {
					volume_final = 0;
				}
				// else, user must have recently untilted the phone (ie. snooze from earlier tilting hasn't "completed" yet, but phone is now untilted, and snooze will end soon)
				else {
					volume_final = Lerp(this.c.sync_volume_min, volume_final, snoozeComp.GetPercentThroughSnoozeEndTransition(this.c.sync_snoozeFadeOffDuration * 1000)!);
				}
			}
		}

		this.siren.UpdateVolume(volume_final);
	}

	//light_player = new EffectPointerPlayer();
	light_brightness!: number;
	Init_Light() {
		//this.light_brightness = this.c.sync_light_brightness_min; // start at min-desync brightness
		this.light_brightness = 0; // start at 0; brightness will be updated soon
	}

	/** Note: This returns false if the belt has not yet been connected. */
	IsBeltDisconnected() {
		const timeSinceLastBreathSample = this.lastForceSampleInfo != null ? Date.now() - this.lastForceSampleInfo.timeProcessed : null;
		const beltDisconnected = timeSinceLastBreathSample != null && timeSinceLastBreathSample >= 10000;
		return beltDisconnected;
	}
	/** Returns true if belt has been loose (ie. not detected enough breath-intervals) for longer than the configured duration. */
	IsBeltLooseForXOrLonger(minTimeOfNoBreathPeaks: number) {
		if (this.currentBeltLoose_startTime == null) return false;
		const timeSinceBeltLoose = Date.now() - this.currentBeltLoose_startTime;
		return timeSinceBeltLoose >= minTimeOfNoBreathPeaks;
	}
	UpdateNonAlertLightEffect_BasedOnState() {
		const phase = this.s.Comp(AlarmsComp).phase;
		if (phase == "drifting") {
			const percentFromMinToMaxBrightness = GetPercentFromXToY(this.c.sync_light_desyncAmountForMinBrightness, this.c.sync_light_desyncAmountForMaxBrightness, this.currentDesync);
			this.light_brightness = Lerp(this.c.sync_light_brightness_min, this.c.sync_light_brightness_max, percentFromMinToMaxBrightness);
			this.UpdateNonAlertLightEffect(true);
		} else if (phase == "dreaming") {
			this.light_brightness = this.c.dream_light_brightness;
			if (this.light_brightness >= 0) {
				this.UpdateNonAlertLightEffect(false); // note: dreaming mode instantly ends once a snooze occurs (see MicroSnoozeComp.StartOrExtendSnooze())
			}
		} else {
			this.light_brightness = 0;
			this.UpdateNonAlertLightEffect(false);
		}
	}
	UpdateNonAlertLightEffect(allowSnoozeToAffect: boolean) {
		let brightness_final = this.light_brightness;

		if (allowSnoozeToAffect) {
			const snoozeComp = this.s.Comp(MicroSnoozeComp);
			// if snooze is active, lock brightness to the brightness-min UNLESS we're near the end of the snooze, in which case we transition to the regular/calculate value
			if (snoozeComp.snooze_active) {
				brightness_final = Lerp(this.c.sync_light_brightness_min, brightness_final, snoozeComp.GetPercentThroughSnoozeEndTransition(this.c.sync_snoozeFadeOffDuration * 1000)!);
			}
		}

		//if (this.s.coreData.alarms_tillAlarmEnd_blockLightEffects) brightness_final = 0;

		//this.light_player.Play(brightness_final);
		this.s.Comp(AlertsComp).SetEffectState("breath_sync", EffectState_Light(true, brightness_final, null));
	}

	override OnStart(recoveryInfo: SessionCoreData|n) {
		if (this.c.deild_attemptStart_extraEffect) {
			this.deild_extraEffectPlayer.effectPointer = this.c.deild_attemptStart_extraEffect;
			this.deild_extraEffectPlayer.OnStart();
		}
	}

	override OnActivate(sessionStarting: boolean) {
		currentBelt.forceListeners.push(this.forceListener);
		currentBelt.AddConnectRequester(this);

		if (this.c.sync_enabled) {
			this.Init_Siren();
			this.StartOrRestartSiren();

			this.Init_Light();
			/*if (this.light_player.effectPointer == null) {
				this.light_player.effectPointer = this.c.sync_light_effectPointer;
				this.light_player.OnStart();
			}*/

			this.doCalcAndUpdateEffectsTimer.Start();
			this.audioRealignmentTimer.Start();
		}
	}
	override OnDeactivate(sessionStopping: boolean) {
		currentBelt.forceListeners.Remove(this.forceListener);
		currentBelt.RemoveConnectRequester(this);
		this.CompleteCurrentDataChunk(); // ensure any remaining data chunk is stored

		// Reset forced pan on native side so audio players return to normal panning
		this.UpdateForcedPanAndMuteStates(null, null, null);

		if (this.c.sync_enabled) {
			this.siren.Stop();
			//this.light_player.Stop();
			this.doCalcAndUpdateEffectsTimer.Stop();
			this.audioRealignmentTimer.Stop();
		}
	}

	WasCpuLockedUpRecently() {
		const now = Date.now();
		const heartbeatsInLast100ms = this.s.Comp(GeneralComp).recentHeartbeats.filter(a=>a.time >= now - 100).length;
		// if <3 heartbeats in last 100ms, CPU was probably locked up on some slow/heavy calc (which skipped heartbeats, and probably delayed eg. receiving/processing of force-samples [android->frontend])
		const cpuLockedUpInLast100ms = heartbeatsInLast100ms < 3;
		return cpuLockedUpInLast100ms;
	}

	lastForceSampleInfo: {sample: number, timeAssigned: number, timeProcessed: number}|n;
	forceListener = (sample: number)=>{
		const now = Date.now();
		const cpuWasLockedUp = this.WasCpuLockedUpRecently();

		let extendFromLastTimeAssigned = this.lastForceSampleInfo != null;
		// if cpu wasn't locked up recently, we can check whether there's a "drift" between sample time-assigned and time-processed (which is substantial, and not just due to a temporary buffer-flushing)
		// (example reason for a "substantial drift" that's not due to a temporary buffer-flushing: if the bluetooth connection was temporarily disrupted, and thus packets/samples were dropped)
		if (!cpuWasLockedUp && this.lastForceSampleInfo) {
			const drift = now.Distance(this.lastForceSampleInfo.timeAssigned);
			// if drift is substantial (ie. nearly reaching the next sample's interval/time-region), then force a sample-data-chunk separation (so that time-assigned remains close to the time-processed)
			if (drift > Belt_sampleInterval * 1.8) {
				this.CompleteCurrentDataChunk();
				extendFromLastTimeAssigned = false;
			}
		}

		const newSampleInfo = {
			sample,
			timeAssigned: extendFromLastTimeAssigned ? this.lastForceSampleInfo!.timeAssigned + Belt_sampleInterval : now,
			timeProcessed: now,
		};
		this.breathProcessor.AddBeltForceSample(sample, newSampleInfo.timeAssigned);
		this.ProgressAndPossiblyStoreCurrentDataChunk(false, newSampleInfo.timeAssigned);

		// Update spatial forced pan based on inhale/exhale direction
		const prevSample = this.lastForceSampleInfo?.sample;
		const sampleChanged = prevSample != null && sample !== prevSample;
		if (sampleChanged) {
			const inhalePanDir = this.c.spatial_inhalePanDirection ?? "right";
			const exhalePanDir: "left" | "right" = inhalePanDir === "right" ? "left" : "right";
			const newForcedPan: "left" | "right" = sample > prevSample ? inhalePanDir : exhalePanDir;

			const inhaleMuteState = this.c.spatial_inhaleMuted;
			const exhaleMuteState = !inhaleMuteState;
			const newForcedMute: boolean = sample > prevSample ? inhaleMuteState : exhaleMuteState;

			const microReversalHappened = newForcedPan !== this.spatial_forcedPan || newForcedMute !== this.spatial_forcedMute;
			if (microReversalHappened) {
				const minReversal = this.c.spatial_minBreathValueReversal ?? 0;
				const reversalLargeEnough = minReversal <= 0 || this._spatial_lastReversalValue == null || Math.abs(sample - this._spatial_lastReversalValue) >= minReversal;
				if (reversalLargeEnough) {
					this.UpdateForcedPanAndMuteStates(newForcedPan, newForcedMute, sample);
				}
			}
		}

		this.lastForceSampleInfo = newSampleInfo;
	};

	// spatial forced panning (based on inhale/exhale detection from breath samples)
	private _spatial_lastReversalValue: number|n = null; // sample value at the last pan reversal, for hysteresis
	spatial_forcedPan: "left" | "right" | null = null;
	spatial_forcedPan_changeCount = 0;
	spatial_forcedMute: boolean | null = null;
	spatial_forcedMute_changeCount = 0;
	UpdateForcedPanAndMuteStates(newForcedPan: "left" | "right" | null, newForcedMute: boolean | null, breathSampleTriggeringChange: number | null) {
		if (!InAndroid(0)) return;
		this._spatial_lastReversalValue = breathSampleTriggeringChange;
		const panStrength = this.c.spatial_panStrength ?? 1;

		if (newForcedPan != this.spatial_forcedPan) {
			this.spatial_forcedPan = newForcedPan;
			this.spatial_forcedPan_changeCount++;
			WebAudio_ForcedPan_Set(this.spatial_forcedPan, panStrength);
			if (this.spatial_forcedPan_changeCount % 2 == 0) {
				WebAudio_ForcedPan_Slow_Toggle();
			}
		}

		if (newForcedMute != this.spatial_forcedMute) {
			this.spatial_forcedMute = newForcedMute;
			this.spatial_forcedMute_changeCount++;
			WebAudio_ForcedMute_Set(this.spatial_forcedMute);
			if (this.spatial_forcedMute_changeCount % 2 == 0) {
				WebAudio_ForcedMute_Slow_Toggle();
			}
		}
	}

	breathProcessor!: BreathProcessor;
	Init_Processors() {
		this.breathProcessor = new BreathProcessor();
		this.breathProcessor.Init_LiveSession({breathCompConfig: this.c});
	}

	// logic for storing data chunks (in IndexedDB, through GDBelt_RawDataDB)
	// ==========

	currentDataChunk: GDBelt_RawDataChunk|n;
	ProgressAndPossiblyStoreCurrentDataChunk(compStoppingOrSuspending: boolean, latestSample_timeAssigned: number) {
		if (this.currentDataChunk == null) {
			this.currentDataChunk = new GDBelt_RawDataChunk({
				sessionId: this.s.coreData.id_live,
				startTime: latestSample_timeAssigned,
				samples_force: [],
			});
		}

		const samplesSinceLastChunk = this.SamplesSinceLastChunk();
		const chunkFilled = samplesSinceLastChunk >= 60 * Belt_samplesPerSecond;

		const storeCurrentChunk = chunkFilled || compStoppingOrSuspending;
		if (storeCurrentChunk) {
			this.CompleteCurrentDataChunk();
		}
	}
	breathProcessor_lastStoredChunk_lastIndex = -1;
	SamplesSinceLastChunk() { return this.breathProcessor.samples_force.length - (this.breathProcessor_lastStoredChunk_lastIndex + 1); }
	CompleteCurrentDataChunk() {
		const samplesSinceLastChunk = this.SamplesSinceLastChunk();
		// if current chunk is null, there's nothing to do
		if (this.currentDataChunk == null) return;
		// if current chunk is empty, don't store it; and actually clear it, so it gets recreated with a less misleading start-time
		if (samplesSinceLastChunk == 0) {
			this.currentDataChunk = null;
			return;
		}
		const newSampleValues = this.breathProcessor.samples_force.slice(this.breathProcessor_lastStoredChunk_lastIndex + 1).map(a=>a.value);
		this.breathProcessor_lastStoredChunk_lastIndex = this.breathProcessor.samples_force.length - 1;

		// store the current chunk
		// (note: mutation is fine since we're the last place that accesses the current instance)
		// ==========

		const chunkToStore = this.currentDataChunk;
		// Persist the chunk in f16 form only: the in-memory `samples_force` field (used everywhere else)
		// is not written to disk; `samples_force_f16` is. The samples in `breathProcessor.samples_force`
		// were already quantized to f16 at the BeltInterface receive boundary, so Float16Array.from()
		// here is a no-op quantization-wise -- it just materializes the on-disk typed-array form.
		// On load, `NormalizeChunkToInMemoryShape` widens the f16 values back into `samples_force: number[]`.
		chunkToStore.samples_force = undefined as any;
		chunkToStore.samples_force_f16 = Float16Array.from(newSampleValues);
		chunkToStore.samples_force_f16_base64 = undefined as any;

		// update storage-tracking now (we assume storage will succeed)
		this.currentDataChunk = null;

		chunksWithStoragePending.push(chunkToStore);
		(async()=>{
			await gdBelt_rawDataDB.StoreChunkForSession(chunkToStore);
			chunksWithStoragePending.Remove(chunkToStore);
		})();
	}
}